import { useContext, useEffect, useState, useCallback } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import { useFormDebounce } from 'hooks/useFormDebounce'
import { apiClient } from 'utils/api/apiClient'
import { UpdatedCalcs } from 'admin/UpdatedCalcs'
import { useRetirementGapCalculations } from 'layouts/authenticated/pages/dashboard/hooks/useRetirementGapCalculations'
import { usePARCOScoreCalculations } from 'layouts/authenticated/pages/dashboard/hooks/usePARCOScoreCalculations'

import Box from '@mui/material/Box'
import { Button, Select, Grid, IconButton } from '@mui/material'
import Typography from '@mui/material/Typography'

// Soft UI Dashboard PRO React components
import OnboardingMultiOptionTile from '../OnboardingMultiOptionTile'
import { callZapier } from 'utils/zapier/callZapier'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowBack, ArrowForwardIosRounded, Close } from '@mui/icons-material'
import Banner from 'atoms/Banner'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

export const LifestyleGoal = ({
  closeModal = () => console.log('Schedule Appointment'),
  isAdmin = false,
  isOnPageForm = true,
  // adjustedWorkingNetTakeHome = 0,
  // adjustedNetTakeHomeInRetirement = 0,
  // adjustedNetTakeHomeDifferencePARCOGap = 0,
  // valueOfInvestableAssets = 0,
  // parcoAnnuityRecommendationCalculator,
}) => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const { updateIntakeStateAndSubmit } = useFormDebounce()
  const [howToLive, setHowToLive] = useState(null)
  const [lifestyleGoal, setLifestyleGoal] = useState(null)

  const [assets, setAssets] = useState([])
  const [liabilities, setLiabilities] = useState([])
  const [deductions, setDeductions] = useState([])
  const [incomes, setIncomes] = useState([])
  const [expenses, setExpenses] = useState([])

  const [assetsLoaded, setAssetsLoaded] = useState(false)

  const getAccounts = useCallback(async () => {
    try {
      const {
        data: { items },
      } = await apiClient.get('api/assets')

      const foundAssets = items?.filter(({ type }) => type === 'asset') || []
      setAssets(foundAssets)

      const foundLiabilities =
        items?.filter(({ type }) => type === 'liability') || []
      setLiabilities(foundLiabilities)

      const foundDeductions =
        items?.filter(({ type }) => type === 'deduction') || []
      setDeductions(foundDeductions)

      const foundIncomes = items?.filter(({ type }) => type === 'income') || []
      setIncomes(foundIncomes)

      const foundExpenses =
        items?.filter(({ type }) => type === 'expense') || []
      setExpenses(foundExpenses)

      setAssetsLoaded(true)
    } catch (error) {
      console.error('Failed to fetch accounts', error)
      setAssetsLoaded(false)
    }
  }, [])

  useEffect(() => {
    getAccounts()
  }, [getAccounts])

  const { valueOfInvestableAssets } = UpdatedCalcs({ assets, liabilities })
  const {
    adjustedNetTakeHomeInRetirement,
    adjustedWorkingNetTakeHome,
    adjustedNetTakeHomeDifferencePARCOGap,
  } = useRetirementGapCalculations(deductions, liabilities, incomes, expenses)
  const { parcoAnnuityRecommendationCalculator } = usePARCOScoreCalculations(
    assets,
    liabilities,
    adjustedWorkingNetTakeHome,
    adjustedNetTakeHomeInRetirement,
    adjustedNetTakeHomeDifferencePARCOGap
  )

  const dollarFormat = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })

  const [isMobile, setIsMobile] = useState(window.innerWidth < 576)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Pagination
  const [open, setOpen] = useState(true)
  const [page, setPage] = useState(0)
  const [direction, setDirection] = useState(1)
  const handleNext = () => {
    setDirection(1)
    setPage((prev) => Math.min(prev + 1, pages.length - 1))
  }
  const handleBack = () => {
    setDirection(-1)
    page === 2
      ? setPage(0)
      : page === 3 && localForm.retirementLifestyle !== 'exact'
      ? setPage(1)
      : setPage((prev) => Math.max(prev - 1, 0))
  }
  const variants = {
    enter: (dir) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
    }),
  }

  const [localForm, setLocalForm] = useState({
    retirementLifestyle:
      intakeState?.retirementLifestyleGoal?.retirementLifestyle || '',
    lifestyleGoal: intakeState?.retirementLifestyleGoal?.lifestyleGoal || 1,
    exactAmount: intakeState?.retirementLifestyleGoal?.exactAmount || '',
  })
  const updateLocalState = (updates, callback) => {
    const newForm = { ...localForm, ...updates }

    setLocalForm(newForm)
    updateIntakeStateAndSubmit(() =>
      updateIntakeStateAtKey({
        retirementLifestyleGoal: newForm,
      })
    )()
    if (callback) callback(newForm)
  }

  // Local-only state update that doesn't trigger global state changes
  const updateLocalStateOnly = (updates, callback) => {
    const newForm = { ...localForm, ...updates }
    setLocalForm(newForm)
    if (callback) callback(newForm)
  }
  const handleOptionClick = (field, value) => {
    if (field === 'retirementLifestyle') {
      if (value === 'same') {
        updateLocalState(
          { retirementLifestyle: 'same', lifestyleGoal: 1 },
          (newForm) => {
            lifestyleGoalNotification(newForm)
            setDirection(1)
            setPage(3)
          }
        )
      } else {
        updateLocalState({ retirementLifestyle: value }, () => {
          handleNext()
        })
      }
    }

    if (field === 'lifestyleGoal') {
      updateLocalState({ lifestyleGoal: value }, (newForm) => {
        lifestyleGoalNotification(newForm)
        setDirection(1)
        setPage(3)
      })
    }
  }
  const handleExactOptionClick = () => {
    // Only update local state, don't trigger global state update
    updateLocalStateOnly({ retirementLifestyle: 'exact', lifestyleGoal: 1 }, () => {
      setDirection(1)
      setPage(2)
    })
  }

  const handleSaveExactOptionClick = () => {
    updateLocalState(
      {
        exactAmount: localForm.exactAmount,
        lifestyleGoal: localForm.exactAmount / adjustedWorkingNetTakeHome,
        updated: true,
      },
      () => {
        console.log('')
      }
    )
    lifestyleGoalNotification()
    handleNext()
  }

  const lifestyleGoalNotification = (formOverride) => {
    const form = formOverride || localForm
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/ut8aqyq/',
      payload: {
        user: {
          firstName: intakeState?.firstName,
          lastName: intakeState?.lastName,
          phoneNumber: intakeState?.phone,
          email: intakeState?.email,
          retirementLifestyleGoal: form,
        },
      },
      sendNotification: false,
    })
  }

  const parcoLifestyleGoal = localForm?.lifestyleGoal || 1
  const parcoGapAdjustedForLifestyleGoal =
    adjustedWorkingNetTakeHome * parcoLifestyleGoal -
    adjustedNetTakeHomeInRetirement

  const parcoAnnuityRecommendation = parcoAnnuityRecommendationCalculator({
    dateOfBirth: intakeState?.dateOfBirth,
    plannedRetirementDate: intakeState?.dateOfPlannedRetirement,
    netIncomeGap: parcoGapAdjustedForLifestyleGoal,
  })
  const parcoAnnuityRecommendationUnadjusted =
    parcoAnnuityRecommendationCalculator({
      dateOfBirth: intakeState?.dateOfBirth,
      plannedRetirementDate: intakeState?.dateOfPlannedRetirement,
      netIncomeGap: adjustedNetTakeHomeDifferencePARCOGap,
    })

  // console.log('parcoAnnuityRecommendation', parcoAnnuityRecommendation)

  const minimumRetirement =
    adjustedNetTakeHomeInRetirement / adjustedWorkingNetTakeHome
  const splitTheGap = (minimumRetirement + 1) / 2
  const affordCurrentLifestyle =
    parcoAnnuityRecommendationUnadjusted?.roundedCurrentAge >= 70
      ? valueOfInvestableAssets >=
        parcoAnnuityRecommendationUnadjusted.fourPercent
      : valueOfInvestableAssets >=
        parcoAnnuityRecommendationUnadjusted.threePercent
  const riskStatusValue = Math.abs(
    valueOfInvestableAssets /
      (parcoAnnuityRecommendation?.roundedCurrentAge >= 70
        ? parcoAnnuityRecommendation.fourPercent
        : parcoAnnuityRecommendation.threePercent) || 0
  )

  const retirementLifestyleOptions = [
    {
      key: 'same',
      title: 'Same',
      subtitle: 'I want to maintain my current lifestyle',
    },
    {
      key: 'different',
      title: 'Different',
      subtitle: 'I want a different lifestyle in retirement',
    },
    {
      key: 'unsure',
      title: "I'm not sure",
      subtitle: "Haven't thought about it, what are my options?",
    },
  ]
  const lifestyleGoalOptions = affordCurrentLifestyle
    ? [
        {
          key: minimumRetirement,
          title: `${Math.round(minimumRetirement * 100)}%`,
          subtitle: 'I want to live off just my pension and social security',
        },
        {
          key: 1,
          title: '100%',
          subtitle: 'I want to maintain my current lifestyle',
        },
        {
          key: 1.25,
          title: '125%',
          subtitle: 'I plan to spend more in retirement',
        },
      ]
    : [
        {
          key: minimumRetirement,
          title: `${Math.round(minimumRetirement * 100)}%`,
          subtitle: 'I want to live off just my pension and social security',
        },
        {
          key: splitTheGap,
          title: `${Math.round(splitTheGap * 100)}%`,
          subtitle: 'I can live off a bit less than my current income',
        },
        {
          key: 1,
          title: '100%',
          subtitle: 'I want to maintain my current lifestyle',
        },
      ]

  const pages = [
    <Grid key="0" item xs={12} sx={{}}>
      <Box>
        <Typography
          variant="headerTitle"
          sx={{ color: '#262627', fontSize: { xs: '24px', sm: '32px' } }}
        >
          How do you want to live in retirement?
        </Typography>
      </Box>
      <Typography variant="body1" mt={{ xs: 2, sm: 3 }}>
        Right now you are used to taking home{' '}
        {dollarFormat.format(adjustedWorkingNetTakeHome)} per month.
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
        mt={4}
      >
        {retirementLifestyleOptions.map((option) => {
          const isSelected = localForm.retirementLifestyle === option.key
          return (
            <Box
              key={option.key}
              onClick={() =>
                handleOptionClick('retirementLifestyle', option.key)
              }
              sx={{
                cursor: 'pointer',
                width: '100%',
                borderRadius: '6px',
                paddingX: 2,
                paddingY: { xs: 1, md: 2 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: isSelected ? '2px solid #262627' : '2px solid #efefef',
                backgroundColor: isSelected ? '#f0f0f0' : 'transparent',
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="h4" color="#262627">
                  {option.title}
                </Typography>
                <Typography
                  variant="body3Weird"
                  color="#262627"
                  sx={{ fontWeight: 'normal', lineHeight: '1.5' }}
                >
                  {option.subtitle}
                </Typography>
              </Box>
              <Box sx={{ ml: 2, display: isMobile ? 'block' : 'none' }}>
                <ArrowForwardIosRounded />
              </Box>
            </Box>
          )
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Typography
          sx={{
            fontSize: '16px',
            color: '#A3a7AA',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          variant="body3Light"
          onClick={handleExactOptionClick}
        >
          Enter an exact amount
        </Typography>
      </Box>
    </Grid>,
    <Grid key="1" item xs={12} mb={3} sx={{}}>
      <Box>
        <Typography
          variant="headerTitle"
          sx={{ color: '#262627', fontSize: { xs: '24px', sm: '32px' } }}
        >
          How much of your current income do you want in Retirement?
        </Typography>
      </Box>
      <Typography variant="body1" mt={3}>
        Select one of the following to see more details:
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
        }}
        mt={4}
      >
        {lifestyleGoalOptions.map((option) => {
          const isSelected = localForm.lifestyleGoal === option.key
          return (
            <Box
              key={option.key}
              onClick={() => handleOptionClick('lifestyleGoal', option.key)}
              sx={{
                cursor: 'pointer',
                width: '100%',
                borderRadius: '6px',
                paddingX: 2,
                paddingY: { xs: 1, md: 2 },
                display: 'flex',
                flexDirection: 'column',
                border: isSelected ? '2px solid #262627' : '2px solid #efefef',
                backgroundColor: isSelected ? '#f0f0f0' : 'transparent',
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Typography variant="h4" color="#262627">
                {option.title}
              </Typography>
              <Typography
                variant="body3Weird"
                color="#262627"
                sx={{ fontWeight: 'normal', lineHeight: '1.5' }}
              >
                {option.subtitle}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Grid>,
    <Grid key="2" item xs={12} sx={{}}>
      <Box>
        <Typography
          variant="headerTitle"
          sx={{ color: '#262627', fontSize: { xs: '24px', sm: '32px' } }}
        >
          How much of your current income do you want in Retirement?
        </Typography>
      </Box>
      <Typography variant="body1" mt={3}>
        Right now you are used to taking home{' '}
        {dollarFormat.format(adjustedWorkingNetTakeHome)} per month. Please
        enter your desired retirement income to adjust your goal:
      </Typography>
      <Box sx={{ display: 'flex' }} mt={4}>
        <Grid xs={12}>
          {renderMuiFormField({
            shrink: true,
            autoFocus: false,
            id: 'retirementIncome',
            label: 'Income in retirement',
            placeholder: dollarFormat.format(adjustedWorkingNetTakeHome),
            inputType: 'currencyFloat',
            value: localForm.exactAmount,
            onChange: (e) =>
              updateLocalState(
                {
                  exactAmount: e,
                  lifestyleGoal: e / adjustedWorkingNetTakeHome,
                },
                () => {
                  console.log('')
                }
              ),
          })}
          <Typography variant="inputNote" component="p">
            This represents{' '}
            {Math.round(
              (localForm.exactAmount / adjustedWorkingNetTakeHome) * 100
            )}
            % of your current take home pay
          </Typography>
        </Grid>
      </Box>
      <Box mt={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button variant="parcoRounded" onClick={handleSaveExactOptionClick}>
          Save
        </Button>
      </Box>
    </Grid>,
    <Grid key="3" item xs={12} mb={3} sx={{}}>
      <Box>
        <Typography variant="headerTitle" sx={{ color: '#262627' }}>
          Retirement Lifestyle Goal Set!
        </Typography>
      </Box>
      <Typography variant="body1" mt={{ xs: 2, md: 3 }}>
        We'll base your Retirement Readiness Score and planning around you
        preserving {Math.round(localForm?.lifestyleGoal * 100) || 100}% of your
        current income.
        {/* {riskStatusValue > (localForm?.lifestyleGoal || 1)
          ? 'Currently you are on track to hit your goal.'
          : 'Currently your goal is at risk and will require attention to hit.'} */}
      </Typography>
      <Box mt={3} sx={{}}>
        <Banner
          // color={
          //   riskStatusValue > (localForm?.lifestyleGoal || 1) ? 'purple' : 'red'
          // }
          color={'purple'}
          arrow={false}
          title="RETIREMENT LIFESTYLE GOAL"
          description={`${
            Math.round(localForm?.lifestyleGoal * 100) || 100
          }% of Current Income`}
        />
      </Box>
    </Grid>,
  ]

  return (
    <>
      <Box
        sx={{
          justifyContent: 'space-between',
          position: 'relative',
          display: `${!isOnPageForm ? 'flex' : 'none'}`,
          top: { xs: -8, sm: -64 },
        }}
      >
        {page > 0 && (
          <IconButton
            onClick={handleBack}
            sx={{
              position: 'absolute',
              top: 0,
              left: { xs: -8, sm: -16 },
              color: '#fff',
              zIndex: 1,
              backgroundColor: '#000',
              '&:hover': {
                backgroundColor: '#000',
              },
            }}
          >
            <ArrowBack />
          </IconButton>
        )}

        <IconButton
          onClick={closeModal}
          sx={{
            position: 'absolute',
            top: 0,
            right: { xs: -8, sm: -16 },
            color: '#fff',
            zIndex: 1,
            backgroundColor: '#000',
            '&:hover': {
              backgroundColor: '#000',
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>
      <Grid
        sx={{ textAlign: 'left', overflow: 'hidden' }}
        px={!isOnPageForm ? { xs: 0, sm: 4 } : 0}
        pt={4}
        pb={{ xs: 2, sm: 4 }}
      >
        {/* <motion.div
          animate={{ height }}
          transition={{ duration: 0.3 }}
          style={{ overflow: 'hidden' }}
        > */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={page}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            style={{ position: 'relative', width: '100%' }}
          >
            {pages[page]}
            <Box display="flex" justifyContent={'flex-end'}>
              {page < pages.length - 1 ? (
                <></>
              ) : (
                <>
                  <Button
                    variant="parcoRounded"
                    onClick={closeModal}
                    sx={{ display: `${!isOnPageForm ? 'flex' : 'none'}` }}
                  >
                    Head to Dashboard
                  </Button>
                </>
              )}
            </Box>
          </motion.div>
        </AnimatePresence>
        {/* </motion.div> */}
      </Grid>
    </>
  )
}
